import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";

import type { MarketplaceOrderStatus } from "../types/marketplace.types";
import {
  getOrderStatusVisual,
  type OrderStatusTone,
} from "./order-status-visual";

/** Badge variants used for marketplace fulfillment and handoff status chips. */
export type MarketplaceStatusBadgeVariant =
  | "default"
  | "slate"
  | "status-sent"
  | "status-in-production"
  | "status-delivered"
  | "status-error";

/** Who is reading the order card; copy may vary, but status hue never does. */
export type MarketplaceFulfillmentViewer = "client" | "ops" | "partner";

const ATTENTION: MarketplaceStatusBadgeVariant = "status-error";
const WAITING: MarketplaceStatusBadgeVariant = "status-sent";
const ACTIVE: MarketplaceStatusBadgeVariant = "status-in-production";
const DONE: MarketplaceStatusBadgeVariant = "status-delivered";
const NEUTRAL: MarketplaceStatusBadgeVariant = "slate";

const FULFILLMENT_BADGE_BY_TONE: Record<
  OrderStatusTone,
  MarketplaceStatusBadgeVariant
> = {
  neutral: NEUTRAL,
  info: WAITING,
  warning: ACTIVE,
  primary: "default",
  danger: ATTENTION,
  success: DONE,
};

export function fulfillmentBadgeVariant(
  status: MarketplaceFulfillmentStatus,
  _viewer?: MarketplaceFulfillmentViewer,
): MarketplaceStatusBadgeVariant {
  return FULFILLMENT_BADGE_BY_TONE[getOrderStatusVisual(status).tone];
}

const HANDOFF_ALERT_STATUSES = [
  "FAILED",
  "PENDING_SEND",
  "SENDING",
  "AWAITING_VENDOR",
] as const satisfies readonly MarketplaceOrderStatus[];

export type HandoffAlertStatus = (typeof HANDOFF_ALERT_STATUSES)[number];

export function isHandoffAlertStatus(
  status: MarketplaceOrderStatus,
): status is HandoffAlertStatus {
  return (HANDOFF_ALERT_STATUSES as readonly string[]).includes(status);
}

/** Vendor email handoff queue — ops sees blocked/failed as red; others mostly passive blue. */
export function handoffBadgeVariant(
  status: HandoffAlertStatus,
  viewer: MarketplaceFulfillmentViewer = "ops",
): MarketplaceStatusBadgeVariant {
  if (viewer === "ops") {
    switch (status) {
      case "FAILED":
      case "AWAITING_VENDOR":
        return ATTENTION;
      case "PENDING_SEND":
        return WAITING;
      case "SENDING":
        return ACTIVE;
    }
  }

  switch (status) {
    case "FAILED":
      return ATTENTION;
    case "AWAITING_VENDOR":
    case "PENDING_SEND":
    case "SENDING":
      return WAITING;
  }
}
